import React from "react";
import StudentLayout from "../../layouts/StudentLayout";
import StudentHeaderSection from "../../components/student/StudentHeaderSection";
import {
  DashboardCardIcon1,
  DashboardCardIcon2,
  DashboardCardIcon3,
  DashboardCardIcon4,
} from "../../components/student/StudentModuleIcons";
import {
  useGetAllCoursesUserSideQuery,
  useGetUserEnrollmentsQuery,
} from "../../services/course";
import EnrolledCourseCard from "../../components/student/EnrolledCourseCard";
import ExploreCourseCard from "../../components/student/ExploreCourseCard";
import { useNavigate } from "react-router-dom";
import { useGetMyCartQuery } from "../../services/cart";

const Dashboard = () => {
  const {
    data: coursesData,
    isLoading: coursesLoading,
    error: coursesError,
  } = useGetAllCoursesUserSideQuery();
  
    const { data: cartResponse } = useGetMyCartQuery();
    const cartItems = cartResponse?.data.items || [];

  const {
    data: enrollmentsData,
    isLoading: enrollmentsLoading,
    error: enrollmentsError,
  } = useGetUserEnrollmentsQuery();

  const exploreCourses =
    coursesData?.data?.map((course) => ({
      id: course._id,
      title: course.courseTitle,
      image: course.courseThumbnail?.secureUrl,
      price: course.coursePrice,
    })) || [];

  const coursesEmptyMessage =
    coursesError?.data?.message ||
    coursesData?.message ||
    "No courses found";

  const isNoCoursesFound =
    coursesError?.status === 404 ||
    (coursesData?.success === false && Boolean(coursesData?.message)) ||
    (!coursesLoading && !coursesError && exploreCourses.length === 0);

  const hasCoursesLoadError = Boolean(coursesError) && !isNoCoursesFound;


  const enrollmentsEmptyMessage =
    (enrollmentsData?.success === false && enrollmentsData?.message) ||
    (enrollmentsError?.data?.success === false &&
      enrollmentsError?.data?.message) ||
    "";

  const hasNoEnrollments =
    typeof enrollmentsEmptyMessage === "string" &&
    enrollmentsEmptyMessage.toLowerCase().includes("no approved enrollments");

  const enrolledCourses = Array.isArray(enrollmentsData?.data)
    ? enrollmentsData.data.filter(
        (item) => item && (item.enrollmentId || item.course),
      )
    : [];

  const navigate = useNavigate();

  const stats = [
    {
      icon: <DashboardCardIcon1 />,
      value: enrolledCourses.length,
      label: "Enrolled Courses",
    },
    {
      icon: <DashboardCardIcon2 />,
      value: coursesData?.pagination?.totalCourses || '0',
      label: "Active Courses",
    },
    {
      icon: <DashboardCardIcon3 />,
      value: "0",
      label: "Completed Courses",
    },
    {
      icon: <DashboardCardIcon4 />,
      value: cartItems.length,
      label: "Saved Courses",
    },
  ];
  return (
    <StudentLayout>
      <div className="hidden sm:block">
        <StudentHeaderSection />
      </div>
      <div className="px-5 lg:px-12 2xl:px-[70px] pb-16  pt-11">
        <h2 className="text-2xl font-semibold text-[#1D2026] tracking-[-1%]">
          Dashboard
        </h2>

        <div className="mt-6 grid gap-5 md:grid-cols-3 lg:grid-cols-4 grid-cols-1 sm:grid-cols-2">
          {stats.map((stat) => (
            <div
              key={stat.label}
              className="flex items-center gap-4 xl:gap-6  bg-white p-4 xl:p-6 shadow-[0_0_5.2px_0_#00000040]"
            >
              <div className="flex h-10 xl:h-[60px] w-10 xl:w-[60px] items-center justify-center bg-[#F5F5F5]">
                {stat.icon}
              </div>
              <div>
                <div className="text-xl md:text-2xl   text-[#1D2026]">{stat.value}</div>
                <p className="mt-1.5 text-sm text-[#4E5566] tracking-[-1%]">
                  {stat.label}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 flex flex-col gap-3 min-[420px]:flex-row min-[420px]:flex-wrap min-[420px]:items-center min-[420px]:gap-4 sm:gap-8 lg:gap-16">
          <button
            type="button"
            className="w-fit px-2.5 py-2 text-left text-base font-semibold leading-7 tracking-[-1%] text-[#FF6636] sm:text-lg sm:leading-8"
          >
            Enrolled Courses
          </button>
          <button
            type="button"
            onClick={() => navigate("/student/upcoming-sessions")}
            className="w-fit px-2.5 py-2 text-left text-base font-semibold leading-7 tracking-[-1%] text-[#070200] sm:text-lg sm:leading-8"
          >
            View Upcoming Sessions
          </button>
        </div>

        {enrollmentsLoading ? (
          <div className="mt-3 text-center text-gray-500">
            Loading enrolled courses...
          </div>
        ) : hasNoEnrollments ||
          (!enrollmentsError && enrolledCourses.length === 0) ? (
          <div className="mt-3 text-center text-gray-500">
            {enrollmentsEmptyMessage || "No approved enrollments found"}
          </div>
        ) : enrollmentsError ? (
          <div className="mt-3 text-center text-red-500">
            {enrollmentsError?.data?.message ||
              "Error loading enrolled courses"}
          </div>
        ) : (
          <div className="mt-6 grid gap-5 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5">
            {enrolledCourses.map((course) => (
              <EnrolledCourseCard key={course.enrollmentId} course={course} />
            ))}
          </div>
        )}

        <div className="mt-8 flex flex-col gap-3 sm:mt-11 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <h3 className="text-xl font-semibold tracking-[-1%] text-[#1D2026] sm:text-2xl">
            Explore Courses
          </h3>
          <button
            type="button"
            onClick={() => navigate("/student/courses")}
            className="w-fit shrink-0 rounded-full px-5 py-2 text-sm font-semibold text-[#FF6636] shadow-[0_0_1.6px_0_#00000040] sm:px-[26px] sm:py-[7px] sm:text-base"
          >
            See all
          </button>
        </div>

        {coursesLoading ? (
          <div className="mt-3 text-center text-gray-500">
            Loading courses...
          </div>
        ) : isNoCoursesFound ? (
          <div className="mt-3 text-center text-gray-500">
            {coursesEmptyMessage}
          </div>
        ) : hasCoursesLoadError ? (
          <div className="mt-3 text-center text-red-500">
            {coursesError?.data?.message || "Error loading courses"}
          </div>
        ) : (
          <div className="mt-3 grid gap-5 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5">
            {exploreCourses.map((course) => (
              <ExploreCourseCard key={course.id} course={course} />
            ))}
          </div>
        )}
      </div>
    </StudentLayout>
  );
};

export default Dashboard;
